/*
 * DP Charts — plant-scoped multi-series history explorer.
 *
 * Layout:
 *   +----------------+---------------------------------------------------+
 *   | Preset panel   |   time range · Rohdaten/Berechnet · raster · export |
 *   +----------------+---------------------------------------------------+
 *   | Multi-leaf     |            multi-series ECharts canvas             |
 *   | picker         |                                                     |
 *   | (virtualised)  +---------------------------------------------------+
 *   |                |            per-series editor (render / colour / …) |
 *   +----------------+---------------------------------------------------+
 *
 * State model:
 * - `selectedIds`: ordered list of leaf ids driven by the picker.
 * - `seriesOptions`: keyed by leaf id, per-series render preferences.
 * - `presetId` + `presetDirty`: which preset (if any) is loaded; dirty flag
 *   flips true whenever the selection or per-series settings drift from the
 *   loaded preset.
 * - `range`, `mode`, `raster`: server-side query parameters. History is fetched
 *   per leaf via the existing /api/leaves/{leafId}/history endpoint
 *   (src/api/history.ts).
 *
 * Fetch strategy:
 * - Each leaf is fetched independently in parallel through the shared fetch
 *   helper. Aborted on selection change so a fast picker doesn't stack
 *   in-flight requests. Series that fail render as an empty line in the chart
 *   but don't block the others (single-widget failure isolation).
 * - Two modes (decision 22.10.2026): "Rohdaten" draws the M4 polyline of all
 *   raw points at the chart width, pixel-identical, for display only.
 *   "Berechnet" draws exact values per raster interval; what is computed is
 *   chosen per series in the series editor (mean, quantity, consumption, …,
 *   see chartCalculations), the raster next to the switch.
 * - Zoom loads finer points for the visible part (debounced): in "Rohdaten"
 *   the shape at the chart width for the zoom window, in "Berechnet" with
 *   interval Auto the finer interval of the zoom window. They are merged into
 *   the points of the whole window (mergeZoomDetail); a fixed interval stays.
 * - Key figures over a window (mean, min, max, quantity/consumption, duty
 *   cycle, range, first/last, reports, discarded reports, coverage) come from the summary
 *   endpoint, exactly from the raw points. The window follows the visible
 *   part of the chart unless entered.
 * - CSV export never uses what the chart shows: it fetches exact interval
 *   values (the series' calculation, in "Rohdaten" its default), with unit,
 *   coverage and marks.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { AppShell } from '../components/AppShell';
import { PlantSubNav } from '../components/PlantSubNav';
import { useAsync } from '../hooks/useAsync';
import { api, ApiError } from '../api/client';
import {
  COMPUTED_LABEL,
  SHAPE_LABEL,
  aggregationLabel,
  autoIntervalMs,
  calculationUnit,
  chartCalculations,
  defaultChartCalculation,
  exactCsv,
  formatIntervalMs,
  historyPath,
  legendLabel,
  mergeZoomDetail,
  parseAggregationKey,
  summaryPath,
  timeSpanParam,
  toSeries,
  type AggregationName,
  type ExactExportColumn,
  type HistoryRequest,
  type LeafHistoryResponse,
  type LeafSummaryResponse,
} from '../api/history';
import { sessionSignal } from '../auth/session';
import { showToast } from '../components/Toaster';
import { CovBufferModal } from '../components/CovBufferModal';
import type {
  ChartAnnotationDto,
  LeafDto,
  ListChartAnnotationsResponse,
  PlantSchemaResponse,
  ChartPresetDto,
  CreateChartPresetRequest,
  ListChartPresetsResponse,
  UpdateChartPresetRequest,
} from '../api/types';
import { MultiLeafPicker } from '../components/charts/MultiLeafPicker';
import {
  MultiSeriesChart,
  seriesColour,
  type AnnotationOverlay,
  type ChartPoint,
  type MultiSeriesChartHandle,
  type SeriesDescriptor,
  type SeriesOptions,
} from '../components/charts/MultiSeriesChart';
import { KeyFigures, type KeyFigureRow, type KeyFigureWindow } from '../components/charts/KeyFigures';
import { PresetPanel } from '../components/charts/PresetPanel';
import { ToolButton, ToolGroup } from '../components/ui/ToolButton';
import {
  IconBrushRange, IconBuffer, IconCalendar, IconComment, IconDownload, IconFullscreen,
  IconImage, IconLive, IconSeries, IconSidebar, IconSigma,
} from '../icons/HvacIcons';
import type { AiPresetApplyPayload } from '../components/charts/AiPresetModal';
import {
  AI_CHART_PRESET_APPLY_EVENT,
  type AiChartPresetApplyDetail,
} from '../components/ai/AiChartPresetLauncher';
import { SeriesEditor } from '../components/charts/SeriesEditor';
import { AnnotationCreator } from '../components/annotations/AnnotationCreator';
import { AnnotationList } from '../components/annotations/AnnotationList';
import { MentionMarkdown } from '../components/annotations/MentionMarkdown';
import './PlantChartsPage.css';

interface Props { id?: string }

// --- Time range presets ----------------------------------------------------
type RangeKey = '1h' | '24h' | '7d' | '30d' | 'custom';
const RANGE_PRESETS: { key: Exclude<RangeKey, 'custom'>; label: string; ms: number }[] = [
  { key: '1h', label: '1 h', ms: 60 * 60_000 },
  { key: '24h', label: '24 h', ms: 24 * 60 * 60_000 },
  { key: '7d', label: '7 d', ms: 7 * 24 * 60 * 60_000 },
  { key: '30d', label: '30 d', ms: 30 * 24 * 60 * 60_000 },
];

interface EffectiveRange { from: Date; to: Date }
interface Range { key: RangeKey; from?: string; to?: string }

function computeRange(range: Range): EffectiveRange {
  if (range.key === 'custom' && range.from && range.to) {
    const from = new Date(range.from);
    const to = new Date(range.to);
    if (Number.isFinite(from.getTime()) && Number.isFinite(to.getTime()) && from < to) {
      return { from, to };
    }
  }
  const preset = RANGE_PRESETS.find((p) => p.key === range.key) ?? RANGE_PRESETS[1];
  const to = new Date();
  const from = new Date(to.getTime() - preset.ms);
  return { from, to };
}

// --- Mode and raster --------------------------------------------------------
/** "Rohdaten" (M4 shape, display only) or "Berechnet" (exact values per raster interval). */
type ChartMode = 'raw' | 'computed';

type RasterKey = 'auto' | '1m' | '5m' | '15m' | '1h' | '1d';
const RASTERS: { key: Exclude<RasterKey, 'auto'>; ms: number }[] = [
  { key: '1m', ms: 60_000 },
  { key: '5m', ms: 5 * 60_000 },
  { key: '15m', ms: 15 * 60_000 },
  { key: '1h', ms: 3600_000 },
  { key: '1d', ms: 86_400_000 },
];
/** More intervals than this per series are not offered: unreadable, and slow. */
const MAX_RASTER_INTERVALS = 20_000;

function rasterMs(raster: RasterKey, windowMs: number): number {
  return RASTERS.find((r) => r.key === raster)?.ms ?? autoIntervalMs(windowMs);
}

/** Calculation of a series in mode "Berechnet": its choice, or the default of its kind. */
function calculationFor(leaf: LeafDto, options: SeriesOptions | undefined): AggregationName {
  const offered = chartCalculations(leaf.metricKind, leaf.valueType);
  const chosen = options?.calculation;
  return chosen && offered.includes(chosen) ? chosen : defaultChartCalculation(leaf.metricKind, leaf.valueType);
}

/** Shape width when the canvas cannot be measured yet. */
const DEFAULT_CHART_WIDTH_PX = 1200;

/** Finer points for the zoomed part of the chart. */
interface ZoomDetail {
  /** detailKey at the time of the request; merged only while it matches. */
  key: string;
  window: [number, number];
  /** Interval of the detail in "Berechnet"; `null` for the shape. */
  intervalMs: number | null;
  series: Record<string, ChartPoint[]>;
}

/** Loaded data of one series. */
interface SeriesData {
  points: ChartPoint[];
  unit: string | null;
  /** Calculation of exact points; `null` for the raw shape. */
  calculation: AggregationName | null;
}

function apiErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: unknown } | null;
    if (body && typeof body.error === 'string') return body.error;
  }
  return err instanceof Error ? err.message : String(err);
}

// --- Preset payload wire shape -------------------------------------------
/**
 * What we serialise into ChartPresetDto.chartSettingsJson. Kept minimal so
 * future settings can be added without invalidating existing presets — the
 * loader tolerates missing fields.
 */
interface ChartSettingsPayload {
  range: Range;
  /** Since 22.10.2026. Missing in older presets: derived from `aggregation`. */
  mode?: ChartMode;
  raster?: RasterKey;
  /** Before 22.10.2026: one aggregation for all series ('shape', 'Mean', and
   *  older 'auto', 'mean', 'sum', …; see parseAggregationKey). Read only. */
  aggregation?: string;
  series: Record<string, SeriesOptions>;
}

/**
 * Mode and per-series calculation from a stored or suggested payload. An old
 * global aggregation becomes mode "Berechnet" with that calculation on every
 * series that offers it; the others keep their default.
 */
function fromLegacyAggregation(
  aggregation: string | undefined,
  series: Record<string, SeriesOptions>,
): { mode: ChartMode; series: Record<string, SeriesOptions> } {
  const parsed = parseAggregationKey(aggregation);
  if (parsed === 'shape') return { mode: 'raw', series };
  const next: Record<string, SeriesOptions> = {};
  for (const [id, o] of Object.entries(series)) next[id] = { ...o, calculation: o.calculation ?? parsed };
  return { mode: 'computed', series: next };
}

const DEFAULT_SERIES_OPTIONS: SeriesOptions = {
  renderType: 'line',
  visible: true,
  smooth: false,
  showThresholds: true,
};

export function PlantChartsPage({ id: plantId }: Props) {
  if (!plantId) {
    return (
      <AppShell title="DP Charts">
        <p class="muted">Anlage aus der Route fehlt.</p>
      </AppShell>
    );
  }

  const schemaState = useAsync(
    (signal) => api.get<PlantSchemaResponse>(`/api/plants/${plantId}/schema`, signal),
    [plantId],
  );

  const plantName = schemaState.status === 'ok' ? schemaState.data.name : '';

  // --- Ansicht: welche Bereiche offen sind (Patrick, 23.10.2026) ----------
  const [view, setView] = useState<ChartView>(readChartView);
  useEffect(() => writeChartView(view), [view]);
  const toggleView = (key: keyof ChartView) => setView((v) => ({ ...v, [key]: !v[key] }));

  // --- Picker + per-series settings ---------------------------------------
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [seriesOptions, setSeriesOptions] = useState<Record<string, SeriesOptions>>({});

  // Every id in selectedIds must have an entry in seriesOptions. Keep them in
  // sync so the SeriesEditor doesn't stutter through undefined lookups.
  useEffect(() => {
    setSeriesOptions((prev) => {
      const next: Record<string, SeriesOptions> = {};
      let changed = false;
      for (const id of selectedIds) {
        if (prev[id]) next[id] = prev[id];
        else { next[id] = { ...DEFAULT_SERIES_OPTIONS }; changed = true; }
      }
      // Drop options for leaves no longer in the selection.
      for (const id of Object.keys(prev)) {
        if (!next[id]) { changed = true; }
      }
      return changed ? next : prev;
    });
  }, [selectedIds]);

  // Flat lookup: id → LeafDto.
  const leafById = useMemo(() => {
    const map = new Map<string, LeafDto>();
    if (schemaState.status !== 'ok') return map;
    const walk = (device: PlantSchemaResponse['devices'][number]) => {
      const walkNode = (n: typeof device.nodes[number]) => {
        for (const l of n.leaves) map.set(l.id, l);
        for (const c of n.children) walkNode(c);
      };
      for (const n of device.nodes) walkNode(n);
    };
    for (const d of schemaState.data.devices) walk(d);
    return map;
  }, [schemaState]);

  // --- Time range / mode / raster -----------------------------------------
  const [range, setRange] = useState<Range>({ key: '24h' });
  const [mode, setMode] = useState<ChartMode>('raw');
  const [raster, setRaster] = useState<RasterKey>('auto');

  // The window of the current range. Recomputed on every refresh tick so a
  // relative range ("24 h") moves with the clock.
  const windowMsFor = (r: Range) => {
    const eff = computeRange(r);
    return eff.to.getTime() - eff.from.getTime();
  };
  const intervalMs = rasterMs(raster, windowMsFor(range));

  // --- Live-Refresh -------------------------------------------------------
  // Wenn eingeschaltet, wird die History-Fetch-useEffect alle 30 Sekunden
  // durch bumping des Refresh-Counters neu getriggert. Wir bumpen aussen
  // im setInterval statt in einem inneren Setter damit der Aborter der
  // vorherigen Fetch aufraeumt (abort() im useEffect-Cleanup).
  const [liveRefresh, setLiveRefresh] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);
  useEffect(() => {
    if (!liveRefresh) return;
    const timer = window.setInterval(() => setRefreshTick((t) => t + 1), 30_000);
    return () => window.clearInterval(timer);
  }, [liveRefresh]);

  // --- History fetch ------------------------------------------------------
  // Calculations only matter in mode "Berechnet"; a change there reloads,
  // a change of colour or line style does not.
  const seriesOptionsRef = useRef(seriesOptions);
  seriesOptionsRef.current = seriesOptions;
  const calculationKey = mode === 'computed'
    ? selectedIds.map((id) => {
      const leaf = leafById.get(id);
      return leaf ? calculationFor(leaf, seriesOptions[id]) : '';
    }).join('|')
    : '';
  const [chartWindow, setChartWindow] = useState<[number, number] | undefined>(undefined);
  /** Visible part of the window while zoomed in, else null. */
  const [zoomed, setZoomed] = useState<[number, number] | null>(null);
  const [seriesData, setSeriesData] = useState<Record<string, SeriesData>>({});
  const [historyBusy, setHistoryBusy] = useState(false);
  const chartCanvasRef = useRef<HTMLDivElement | null>(null);
  // Last reported load error; with live refresh the same error would
  // otherwise toast every 30 s.
  const lastLoadErrorRef = useRef<string | null>(null);

  useEffect(() => {
    // Abort in-flight requests when the selection or range changes — the
    // user may be shuffling leaves fast and stacking dozens of stale
    // requests would waste bandwidth and jitter the chart on late arrivals.
    if (selectedIds.length === 0) {
      setSeriesData({});
      return;
    }
    const controller = new AbortController();
    setHistoryBusy(true);
    const eff = computeRange(range);
    setChartWindow([eff.from.getTime(), eff.to.getTime()]);
    setZoomed(null);
    const targets = [...selectedIds];
    const widthPx = chartCanvasRef.current?.clientWidth || DEFAULT_CHART_WIDTH_PX;
    const errors: string[] = [];

    (async () => {
      const results = await Promise.all(targets.map(async (id) => {
        const leaf = leafById.get(id);
        const calculation = leaf ? calculationFor(leaf, seriesOptionsRef.current[id]) : null;
        const request: HistoryRequest = mode === 'computed' && calculation
          ? { mode: 'exact', aggregation: calculation, interval: timeSpanParam(intervalMs) }
          : { mode: 'shape', widthPx };
        try {
          const res = await api.get<LeafHistoryResponse>(
            historyPath(id, eff.from, eff.to, request),
            controller.signal,
          );
          return { id, data: { points: toSeries(res), unit: res.unit, calculation: request.mode === 'exact' ? calculation : null } };
        } catch (err) {
          if (controller.signal.aborted) return null;
          if (!(err instanceof ApiError && err.status === 404)) {
            // 404: no history yet. Anything else (e.g. 422, too many raw
            // points for exact values) is reported once below. Either way
            // the series renders empty and the others still render.
            errors.push(apiErrorMessage(err));
          }
          return { id, data: { points: [] as ChartPoint[], unit: leafById.get(id)?.unit ?? null, calculation: null } };
        }
      }));
      if (controller.signal.aborted) return;
      const next: Record<string, SeriesData> = {};
      for (const r of results) {
        if (r) next[r.id] = r.data;
      }
      setSeriesData(next);
      setHistoryBusy(false);
      const error = errors[0] ?? null;
      if (error !== null && error !== lastLoadErrorRef.current) {
        showToast({ tone: 'warn', title: 'Verlauf unvollständig', message: error });
      }
      lastLoadErrorRef.current = error;
    })();

    return () => {
      controller.abort();
      setHistoryBusy(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIds.join('|'), range.key, range.from, range.to, mode, intervalMs, calculationKey, refreshTick, leafById]);

  // --- Finer points for the zoomed part ------------------------------------
  // What the detail must match to be merged: a change of mode, interval,
  // calculation or selection makes older detail unusable.
  const detailKey = `${mode}|${intervalMs}|${calculationKey}|${selectedIds.join('|')}`;
  const [detail, setDetail] = useState<ZoomDetail | null>(null);
  const [detailBusy, setDetailBusy] = useState(false);

  useEffect(() => {
    if (!zoomed || selectedIds.length === 0 || historyBusy) {
      if (!zoomed) setDetail(null);
      return;
    }
    const [from, to] = zoomed;
    // In "Berechnet" only interval Auto gets finer; a chosen interval stays.
    const detailIntervalMs = mode === 'computed' ? autoIntervalMs(to - from) : null;
    if (mode === 'computed' && (raster !== 'auto' || detailIntervalMs === null || detailIntervalMs >= intervalMs)) {
      setDetail(null);
      return;
    }
    const controller = new AbortController();
    const key = detailKey;
    const targets = [...selectedIds];
    const widthPx = chartCanvasRef.current?.clientWidth || DEFAULT_CHART_WIDTH_PX;
    const timer = window.setTimeout(() => {
      setDetailBusy(true);
      (async () => {
        const entries = await Promise.all(targets.map(async (id) => {
          const leaf = leafById.get(id);
          const calculation = leaf ? calculationFor(leaf, seriesOptionsRef.current[id]) : null;
          const request: HistoryRequest = mode === 'computed' && calculation && detailIntervalMs !== null
            ? { mode: 'exact', aggregation: calculation, interval: timeSpanParam(detailIntervalMs) }
            : { mode: 'shape', widthPx };
          try {
            const res = await api.get<LeafHistoryResponse>(
              historyPath(id, new Date(from), new Date(to), request),
              controller.signal,
            );
            return [id, toSeries(res)] as const;
          } catch {
            // The base points stay; the error, if any, was reported with them.
            return null;
          }
        }));
        if (controller.signal.aborted) return;
        const series: Record<string, ChartPoint[]> = {};
        for (const e of entries) if (e) series[e[0]] = e[1];
        setDetail({ key, window: [from, to], intervalMs: detailIntervalMs, series });
        setDetailBusy(false);
      })();
    }, 400);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
      setDetailBusy(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoomed?.[0], zoomed?.[1], detailKey, seriesData, historyBusy]);

  // --- Series descriptors passed to the chart ----------------------------
  const chartSeries: SeriesDescriptor[] = useMemo(() => {
    const usable = detail && detail.key === detailKey ? detail : null;
    // Legend and unit name the interval the visible part is computed with.
    const shownIntervalMs = usable?.intervalMs ?? intervalMs;
    const out: SeriesDescriptor[] = [];
    for (const id of selectedIds) {
      const leaf = leafById.get(id);
      if (!leaf) continue;
      const data = seriesData[id];
      const name = leaf.displayName || leaf.symbol;
      const calc = data?.calculation ?? null;
      const base = data?.points ?? [];
      const finer = usable?.series[id];
      out.push({
        leaf,
        points: finer ? mergeZoomDetail(base, finer, usable!.window) : base,
        unit: data ? (calc ? calculationUnit(calc, data.unit, shownIntervalMs) : data.unit) : undefined,
        label: calc ? legendLabel(name, calc, leaf.metricKind, leaf.valueType, shownIntervalMs) : name,
        stepped: true,
        options: seriesOptions[id] ?? DEFAULT_SERIES_OPTIONS,
      });
    }
    return out;
  }, [selectedIds, leafById, seriesData, seriesOptions, intervalMs, detail, detailKey]);

  // --- Key figures --------------------------------------------------------
  const [kpiWindow, setKpiWindow] = useState<KeyFigureWindow>({ mode: 'view' });
  const kpiRange = useMemo((): { from: Date; to: Date } | null => {
    if (kpiWindow.mode === 'custom') {
      const from = new Date(kpiWindow.from);
      const to = new Date(kpiWindow.to);
      return Number.isFinite(from.getTime()) && Number.isFinite(to.getTime()) && from < to ? { from, to } : null;
    }
    const w = zoomed ?? chartWindow;
    return w ? { from: new Date(w[0]), to: new Date(w[1]) } : null;
  }, [kpiWindow, zoomed, chartWindow]);
  const [summaries, setSummaries] = useState<Record<string, { summary?: LeafSummaryResponse; error?: string }>>({});
  const [kpiBusy, setKpiBusy] = useState(false);

  useEffect(() => {
    if (selectedIds.length === 0 || !kpiRange) {
      setSummaries({});
      return;
    }
    const controller = new AbortController();
    // Zooming fires many events; wait until it settles.
    const timer = window.setTimeout(() => {
      setKpiBusy(true);
      (async () => {
        const entries = await Promise.all(selectedIds.map(async (id) => {
          try {
            const summary = await api.get<LeafSummaryResponse>(summaryPath(id, kpiRange.from, kpiRange.to), controller.signal);
            return [id, { summary }] as const;
          } catch (err) {
            return [id, { error: apiErrorMessage(err) }] as const;
          }
        }));
        if (controller.signal.aborted) return;
        setSummaries(Object.fromEntries(entries));
        setKpiBusy(false);
      })();
    }, 300);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
      setKpiBusy(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIds.join('|'), kpiRange?.from.getTime(), kpiRange?.to.getTime(), refreshTick]);

  const kpiRows: KeyFigureRow[] = selectedIds
    .map((id, index): KeyFigureRow | null => {
      const leaf = leafById.get(id);
      if (!leaf) return null;
      return {
        leaf,
        colour: seriesColour(seriesOptions[id] ?? DEFAULT_SERIES_OPTIONS, index),
        summary: summaries[id]?.summary,
        error: summaries[id]?.error,
      };
    })
    .filter((r): r is KeyFigureRow => r !== null);

  // Empty chart: say so, and when the data point last reported.
  const chartEmpty = !historyBusy
    && chartSeries.length > 0
    && Object.keys(seriesData).length > 0
    && chartSeries.every((s) => s.points.every((p) => p[1] === null));
  const kpiMatchesChart = chartEmpty && kpiWindow.mode === 'view' && zoomed === null;
  const lastReport = kpiMatchesChart
    ? kpiRows
      .map((r) => r.summary?.lastReportAt)
      .filter((t): t is string => typeof t === 'string')
      .sort()
      .pop()
    : undefined;
  // Reports there were, but none valid: say so instead of "no data".
  const invalidOnly = kpiMatchesChart
    ? kpiRows.reduce((n, r) => n + (r.summary?.invalidCount ?? 0), 0)
    : 0;

  // --- Preset state -------------------------------------------------------
  const [presets, setPresets] = useState<ChartPresetDto[]>([]);
  const [presetsLoading, setPresetsLoading] = useState(false);
  const [currentPresetId, setCurrentPresetId] = useState<string | null>(null);
  // Snapshot of the last loaded/saved payload so we can detect dirty edits
  // without recomputing on every keystroke.
  const loadedSnapshotRef = useRef<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setPresetsLoading(true);
    (async () => {
      try {
        const res = await api.get<ListChartPresetsResponse>(
          `/api/chart-presets?plantId=${plantId}`,
          controller.signal,
        );
        if (controller.signal.aborted) return;
        setPresets(res.presets);
      } catch (err) {
        if (controller.signal.aborted) return;
        // eslint-disable-next-line no-console
        console.warn('Preset load failed', err);
      } finally {
        if (!controller.signal.aborted) setPresetsLoading(false);
      }
    })();
    return () => controller.abort();
  }, [plantId]);

  // --- Annotations ---------------------------------------------------------
  // Fetched once per plant and after every save/delete. Rendered as an
  // ECharts markLine/markArea overlay by MultiSeriesChart.
  const [annotations, setAnnotations] = useState<ChartAnnotationDto[]>([]);
  const [annotationsRefreshKey, setAnnotationsRefreshKey] = useState(0);
  const [creatorSeed, setCreatorSeed] = useState<
    { instant: number } | { range: [number, number] } | null
  >(null);
  const [detailAnnotation, setDetailAnnotation] = useState<ChartAnnotationDto | null>(null);
  const [showAnnotationList, setShowAnnotationList] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      try {
        const res = await api.get<ListChartAnnotationsResponse>(
          `/api/plants/${plantId}/annotations`,
          controller.signal,
        );
        if (!controller.signal.aborted) setAnnotations(res.annotations);
      } catch (err) {
        if (!controller.signal.aborted) {
          // eslint-disable-next-line no-console
          console.warn('Annotation load failed', err);
        }
      }
    })();
    return () => controller.abort();
  }, [plantId, annotationsRefreshKey]);

  // Deep-link handling: /plants/{id}/charts?annotation={sourceId}. On mount
  // (or annotation-list arrival) find the target row, jump the range to fit
  // it, and pop the detail popover.
  const deepLinkAppliedRef = useRef(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (deepLinkAppliedRef.current) return;
    const wanted = new URLSearchParams(window.location.search).get('annotation');
    if (!wanted) return;
    const hit = annotations.find((a) => a.id === wanted);
    if (!hit) return; // wait for the list to arrive
    deepLinkAppliedRef.current = true;
    setDetailAnnotation(hit);
    // Fit the chart range to the annotation with a 25% pad on either side.
    const start = new Date(hit.atUtc).getTime();
    const end = hit.untilUtc ? new Date(hit.untilUtc).getTime() : start;
    const span = Math.max(end - start, 60 * 60_000);
    const pad = span * 0.25;
    setRange({
      key: 'custom',
      from: toLocalIsoNoMs(new Date(start - pad)),
      to: toLocalIsoNoMs(new Date(end + pad)),
    });
  }, [annotations]);

  const annotationOverlays: AnnotationOverlay[] = useMemo(() =>
    annotations.map((a) => ({
      id: a.id,
      kind: a.kind,
      atMs: new Date(a.atUtc).getTime(),
      untilMs: a.untilUtc ? new Date(a.untilUtc).getTime() : null,
      label: `${a.authorDisplayName}: ${a.body.slice(0, 80)}`,
    })), [annotations]);

  const currentSnapshot = useMemo(() => JSON.stringify({
    ids: selectedIds,
    settings: seriesOptions,
    range,
    mode,
    raster,
  }), [selectedIds, seriesOptions, range, mode, raster]);

  const hasUnsavedChanges = currentPresetId !== null
    && loadedSnapshotRef.current !== null
    && loadedSnapshotRef.current !== currentSnapshot;

  const loadPreset = useCallback((preset: ChartPresetDto) => {
    // Mit geladenem Preset steht der Chart im Vordergrund; die Auswahl
    // bleibt per Knopf erreichbar.
    setView((v) => ({ ...v, picker: false }));
    try {
      const ids: string[] = JSON.parse(preset.leafIdsJson);
      const payload: ChartSettingsPayload = JSON.parse(preset.chartSettingsJson);
      setSelectedIds(Array.isArray(ids) ? ids.filter((x) => typeof x === 'string') : []);
      let opts: Record<string, SeriesOptions> = {};
      for (const id of ids) {
        opts[id] = { ...DEFAULT_SERIES_OPTIONS, ...(payload.series?.[id] ?? {}) };
      }
      let presetMode: ChartMode;
      if (payload.mode === 'raw' || payload.mode === 'computed') {
        presetMode = payload.mode;
      } else {
        const legacy = fromLegacyAggregation(payload.aggregation, opts);
        presetMode = legacy.mode;
        opts = legacy.series;
      }
      const presetRaster: RasterKey = payload.raster ?? 'auto';
      setSeriesOptions(opts);
      if (payload.range) setRange(payload.range);
      setMode(presetMode);
      setRaster(presetRaster);
      setCurrentPresetId(preset.id);
      // Set the snapshot AFTER the state settles by using the just-computed
      // reference values.
      loadedSnapshotRef.current = JSON.stringify({
        ids,
        settings: opts,
        range: payload.range ?? range,
        mode: presetMode,
        raster: presetRaster,
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Preset payload parse failed', err);
      showToast({ tone: 'error', title: 'Preset konnte nicht geladen werden', message: 'Ungültiges Payload-Format.' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buildPayload = useCallback((): { leafIdsJson: string; chartSettingsJson: string } => {
    const settings: Record<string, SeriesOptions> = {};
    for (const id of selectedIds) {
      settings[id] = seriesOptions[id] ?? DEFAULT_SERIES_OPTIONS;
    }
    const payload: ChartSettingsPayload = { range, mode, raster, series: settings };
    return {
      leafIdsJson: JSON.stringify(selectedIds),
      chartSettingsJson: JSON.stringify(payload),
    };
  }, [selectedIds, seriesOptions, range, mode, raster]);

  const saveNewPreset = async (name: string, scope: 'user' | 'plant'): Promise<void> => {
    const { leafIdsJson, chartSettingsJson } = buildPayload();
    const body: CreateChartPresetRequest = {
      name, scope, plantId, groupId: null, leafIdsJson, chartSettingsJson,
    };
    try {
      const created = await api.post<ChartPresetDto>('/api/chart-presets', body);
      setPresets((prev) => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)));
      setCurrentPresetId(created.id);
      loadedSnapshotRef.current = currentSnapshot;
      showToast({ tone: 'success', title: 'Preset gespeichert', message: name });
    } catch (err) {
      showToast({ tone: 'error', title: 'Speichern fehlgeschlagen', message: err instanceof Error ? err.message : String(err) });
      throw err;
    }
  };

  const updateCurrentPreset = async (): Promise<void> => {
    if (!currentPresetId) return;
    const preset = presets.find((p) => p.id === currentPresetId);
    if (!preset) return;
    const { leafIdsJson, chartSettingsJson } = buildPayload();
    const body: UpdateChartPresetRequest = {
      name: preset.name,
      scope: preset.scope.toLowerCase() as 'user' | 'plant' | 'group',
      groupId: preset.groupId,
      leafIdsJson,
      chartSettingsJson,
    };
    try {
      const updated = await api.put<ChartPresetDto>(`/api/chart-presets/${preset.id}`, body);
      setPresets((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
      loadedSnapshotRef.current = currentSnapshot;
      showToast({ tone: 'success', title: 'Preset aktualisiert', message: preset.name });
    } catch (err) {
      showToast({ tone: 'error', title: 'Aktualisieren fehlgeschlagen', message: err instanceof Error ? err.message : String(err) });
      throw err;
    }
  };

  const renameCurrentPreset = async (name: string, scope: 'user' | 'plant'): Promise<void> => {
    if (!currentPresetId) return;
    const preset = presets.find((p) => p.id === currentPresetId);
    if (!preset) return;
    // Beim reinen Rename/Rescope wollen wir den Chart-Payload verbatim
    // weitergeben — sonst wuerde jedes "Bearbeiten…" die auf dem Server
    // liegende Auswahl mit der aktuellen UI-Auswahl ueberschreiben. Server
    // erwartet die JSONs im UpdateRequest immer, also durchreichen.
    const body: UpdateChartPresetRequest = {
      name,
      scope,
      groupId: null,
      leafIdsJson: preset.leafIdsJson,
      chartSettingsJson: preset.chartSettingsJson,
    };
    try {
      const updated = await api.put<ChartPresetDto>(`/api/chart-presets/${preset.id}`, body);
      setPresets((prev) => prev.map((p) => (p.id === updated.id ? updated : p))
        .sort((a, b) => a.name.localeCompare(b.name)));
      showToast({ tone: 'success', title: 'Preset aktualisiert', message: name });
    } catch (err) {
      showToast({ tone: 'error', title: 'Bearbeiten fehlgeschlagen', message: err instanceof Error ? err.message : String(err) });
      throw err;
    }
  };

  // Der globale AI-Assistant-Button (AppShell) rendert unseren Chart-Preset-
  // Launcher der beim Uebernehmen ein Window-Custom-Event feuert. Hier
  // horchen wir darauf und uebertragen den Vorschlag in die aktuelle
  // Auswahl. Kein direkter Modal-State auf dieser Seite noetig — der Modal
  // lebt vollstaendig unter dem AI-Button.
  useEffect(() => {
    const onApply = (e: Event) => {
      const detail = (e as CustomEvent<AiChartPresetApplyDetail>).detail;
      if (detail) applyAiSuggestion(detail);
    };
    window.addEventListener(AI_CHART_PRESET_APPLY_EVENT, onApply);
    return () => window.removeEventListener(AI_CHART_PRESET_APPLY_EVENT, onApply);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyAiSuggestion = ({ suggestion, acceptedSeries }: AiPresetApplyPayload): void => {
    // Der KI-Vorschlag ersetzt die aktuelle Auswahl komplett — sonst wuerde
    // der User bei jedem "Uebernehmen" die alte Auswahl neben der neuen
    // stehen haben. Er kann jederzeit Save-as speichern; wir hydrieren also
    // die selectedIds + seriesOptions + Range + Aggregation, aber lassen
    // currentPresetId auf null (Vorschlag ist noch kein persistiertes Preset).
    const ids: string[] = [];
    const opts: Record<string, SeriesOptions> = {};
    for (const s of acceptedSeries) {
      ids.push(s.leafId);
      opts[s.leafId] = {
        ...DEFAULT_SERIES_OPTIONS,
        renderType: (s.renderType as SeriesOptions['renderType']) ?? DEFAULT_SERIES_OPTIONS.renderType,
        lineStyle: (s.lineStyle ?? DEFAULT_SERIES_OPTIONS.lineStyle) as SeriesOptions['lineStyle'],
        strokeWidth: s.strokeWidth ?? DEFAULT_SERIES_OPTIONS.strokeWidth,
        axisGroup: s.axisGroup ?? DEFAULT_SERIES_OPTIONS.axisGroup,
        smooth: s.smooth ?? DEFAULT_SERIES_OPTIONS.smooth,
        showThresholds: s.showThresholds ?? DEFAULT_SERIES_OPTIONS.showThresholds,
      };
    }
    const legacy = fromLegacyAggregation(suggestion.aggregation, opts);
    setSelectedIds(ids);
    setSeriesOptions(legacy.series);
    setRange({ key: suggestion.rangeKey });
    setMode(legacy.mode);
    setRaster('auto');
    setCurrentPresetId(null);
    loadedSnapshotRef.current = null;
    showToast({
      tone: 'success',
      title: 'KI-Vorschlag übernommen',
      message: `${suggestion.name} — ${acceptedSeries.length} Serien`,
    });
  };

  const deletePreset = async (preset: ChartPresetDto): Promise<void> => {
    try {
      await api.delete(`/api/chart-presets/${preset.id}`);
      setPresets((prev) => prev.filter((p) => p.id !== preset.id));
      if (currentPresetId === preset.id) {
        setCurrentPresetId(null);
        loadedSnapshotRef.current = null;
      }
      showToast({ tone: 'success', title: 'Preset gelöscht', message: preset.name });
    } catch (err) {
      showToast({ tone: 'error', title: 'Löschen fehlgeschlagen', message: err instanceof Error ? err.message : String(err) });
      throw err;
    }
  };

  // --- Gepufferter Verlauf ------------------------------------------------
  // Der Puffer des Field-Clients ist eine andere Datenquelle als die
  // Historie: er liegt auf dem Geraet, enthaelt auch nie gesendete
  // Datenpunkte und ist nach 30 Tagen weg. Deshalb ein eigenes Fenster und
  // kein weiterer Knopf am Chart - die Uebernahme in die Historie ist nicht
  // zurueckzunehmen und darf nicht neben PNG und CSV stehen.
  const [bufferOpen, setBufferOpen] = useState(false);

  // --- Export ------------------------------------------------------------
  const chartHandleRef = useRef<MultiSeriesChartHandle | null>(null);

  const exportPng = () => {
    const url = chartHandleRef.current?.getPngDataUrl();
    if (!url) {
      showToast({ tone: 'warn', title: 'Kein Chart', message: 'Nichts zu exportieren.' });
      return;
    }
    const a = document.createElement('a');
    a.href = url;
    a.download = presetFilename('png');
    a.click();
  };

  // CSV always holds exact interval values, never the shape on screen: in
  // "Berechnet" each series' calculation and the raster, in "Rohdaten" each
  // data point's default (mean, consumption, last value) on the raster.
  const [exportBusy, setExportBusy] = useState(false);
  const exportCsv = async () => {
    const eff = computeRange(range);
    const leaves = selectedIds
      .map((id) => leafById.get(id))
      .filter((leaf): leaf is LeafDto => leaf !== undefined);
    if (leaves.length === 0) {
      showToast({ tone: 'warn', title: 'Keine Daten', message: 'Nichts zu exportieren.' });
      return;
    }
    setExportBusy(true);
    let columns: ExactExportColumn[];
    try {
      columns = await Promise.all(leaves.map(async (leaf) => {
        const applied: AggregationName = mode === 'computed'
          ? calculationFor(leaf, seriesOptions[leaf.id])
          : defaultChartCalculation(leaf.metricKind, leaf.valueType);
        const response = await api.get<LeafHistoryResponse>(
          historyPath(leaf.id, eff.from, eff.to, {
            mode: 'exact',
            aggregation: applied,
            interval: timeSpanParam(rasterMs(raster, eff.to.getTime() - eff.from.getTime())),
          }),
        );
        return {
          name: leaf.displayName || leaf.symbol,
          aggregation: applied,
          metricKind: leaf.metricKind,
          valueType: leaf.valueType,
          response,
        };
      }));
    } catch (err) {
      showToast({ tone: 'error', title: 'CSV-Export fehlgeschlagen', message: apiErrorMessage(err) });
      return;
    } finally {
      setExportBusy(false);
    }
    const csv = exactCsv(columns);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = presetFilename('csv');
    a.click();
    // Chrome revokes eventually; do it explicitly so the blob URL doesn't
    // hang around until GC.
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  };

  const openFullscreen = () => {
    // Open the current page in a new tab with a `?fullscreen=1` marker; the
    // page reads that on mount and hides the sub-nav. Simpler than a
    // dedicated route because the state (selection, range, per-series) lives
    // in the loaded preset and doesn't need re-computation server-side.
    //
    // Wenn ein Preset aktiv ist, haengen wir dessen id als `preset=` an. Der
    // Mount-Effekt weiter unten liest das und ruft loadPreset auf, sobald die
    // Presets-Liste geladen ist. Ohne diesen Parameter wuerde der neue Tab
    // mit einer leeren Auswahl aufwachen — der User haette den Vollbild-Klick
    // dann quasi als "Reset" wahrgenommen.
    const url = new URL(window.location.href);
    url.searchParams.set('fullscreen', '1');
    if (currentPresetId) url.searchParams.set('preset', currentPresetId);
    window.open(url.toString(), '_blank', 'noopener');
  };

  // Beim Laden per Query-Param (`?preset=...`) das Preset auto-laden sobald
  // die Presets-Liste vom Server angekommen ist. Feuert genau einmal pro
  // Match — wenn der User anschliessend manuell ein anderes Preset auswaehlt,
  // reagiert dieser Effekt nicht mehr auf denselben Query-Param.
  const autoLoadedRef = useRef<string | null>(null);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const targetId = new URLSearchParams(window.location.search).get('preset');
    if (!targetId) return;
    if (autoLoadedRef.current === targetId) return;
    const hit = presets.find((p) => p.id === targetId);
    if (!hit) return;
    autoLoadedRef.current = targetId;
    loadPreset(hit);
  }, [presets, loadPreset]);

  const isFullscreen = typeof window !== 'undefined'
    && new URLSearchParams(window.location.search).get('fullscreen') === '1';

  function presetFilename(ext: string, suffix?: string): string {
    const preset = currentPresetId ? presets.find((p) => p.id === currentPresetId) : null;
    const base = preset ? preset.name : `charts-${plantName || plantId}`;
    const safe = base.replace(/[^\w\-]+/g, '_');
    const ts = new Date().toISOString().replace(/[:T]/g, '-').slice(0, 16);
    return `${safe}${suffix ? `_${suffix}` : ''}_${ts}${ext ? `.${ext}` : ''}`;
  }

  // --- Render -----------------------------------------------------------
  const editorRows = selectedIds
    .map((id, index) => {
      const leaf = leafById.get(id);
      if (!leaf) return null;
      const options = seriesOptions[id] ?? DEFAULT_SERIES_OPTIONS;
      return {
        leaf,
        options,
        colour: seriesColour(options, index),
        calculations: chartCalculations(leaf.metricKind, leaf.valueType).map((value) => ({
          value,
          label: aggregationLabel(value, leaf.metricKind, leaf.valueType),
        })),
        calculation: calculationFor(leaf, options),
      };
    })
    .filter((r): r is NonNullable<typeof r> => r !== null);

  const windowMs = windowMsFor(range);

  const patchSeries = (id: string, patch: Partial<SeriesOptions>) => {
    setSeriesOptions((prev) => ({ ...prev, [id]: { ...(prev[id] ?? DEFAULT_SERIES_OPTIONS), ...patch } }));
  };

  const removeSeries = (id: string) => {
    setSelectedIds((prev) => prev.filter((x) => x !== id));
  };

  return (
    <AppShell title="DP Charts">
      {!isFullscreen && <PlantSubNav plantId={plantId} plantName={plantName} />}
      <div class={`hv-charts${isFullscreen ? ' hv-charts--fullscreen' : ''}`}>
        <div class="hv-charts__toolbar">
          {!isFullscreen && (
            <ToolGroup label="Ansicht">
              <ToolButton label={view.picker ? 'Datenpunkte ausblenden' : 'Datenpunkte einblenden'}
                          active={view.picker} onClick={() => toggleView('picker')}>
                <IconSidebar size={18} />
              </ToolButton>
              <ToolButton label={view.series ? 'Serien ausblenden' : 'Serien einblenden'}
                          active={view.series} onClick={() => toggleView('series')}>
                <IconSeries size={18} />
              </ToolButton>
              <ToolButton label={view.kpi ? 'Kennzahlen ausblenden' : 'Kennzahlen einblenden'}
                          active={view.kpi} onClick={() => toggleView('kpi')}>
                <IconSigma size={18} />
              </ToolButton>
            </ToolGroup>
          )}
          <PresetPanel
            presets={presets}
            loading={presetsLoading}
            currentPresetId={currentPresetId}
            currentUserId={sessionSignal.value.user?.userId ?? null}
            hasUnsavedChanges={hasUnsavedChanges}
            selectedLeafCount={selectedIds.length}
            onLoad={loadPreset}
            onSaveNew={saveNewPreset}
            onUpdateCurrent={updateCurrentPreset}
            onRenameCurrent={renameCurrentPreset}
            onDelete={deletePreset}
          />
          <div class="hv-charts__seg" role="group" aria-label="Zeitraum">
            {RANGE_PRESETS.map((p) => (
              <button
                key={p.key}
                type="button"
                class={`hv-charts__seg-btn${range.key === p.key ? ' is-active' : ''}`}
                onClick={() => setRange({ key: p.key })}
                title={`Zeitraum ${p.label}`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <ToolGroup label="Zeitraum frei">
            <ToolButton
              label="Zeitraum frei wählen"
              active={range.key === 'custom'}
              onClick={() => {
                if (range.key === 'custom') { setRange({ key: '24h' }); return; }
                const now = new Date();
                const from = new Date(now.getTime() - 24 * 60 * 60_000);
                setRange({ key: 'custom', from: toLocalIsoNoMs(from), to: toLocalIsoNoMs(now) });
              }}
            >
              <IconCalendar size={18} />
            </ToolButton>
          </ToolGroup>
          {range.key === 'custom' && (
            <div class="hv-charts__custom-range">
              <input
                type="datetime-local"
                value={range.from ?? ''}
                onChange={(e) => setRange({ ...range, from: (e.currentTarget as HTMLInputElement).value })}
              />
              <span class="muted">bis</span>
              <input
                type="datetime-local"
                value={range.to ?? ''}
                onChange={(e) => setRange({ ...range, to: (e.currentTarget as HTMLInputElement).value })}
              />
            </div>
          )}
          {/* Rohdaten | Berechnet und das Intervall: das Intervall steht
              immer da, damit ein Moduswechsel die Leiste nicht verschiebt. */}
          <div class="hv-charts__seg" role="group" aria-label="Darstellung">
            <button
              type="button"
              class={`hv-charts__seg-btn${mode === 'raw' ? ' is-active' : ''}`}
              onClick={() => setMode('raw')}
              title="Jede Meldung des Reglers als Treppe, ohne Rechnung"
            >
              {SHAPE_LABEL}
            </button>
            <button
              type="button"
              class={`hv-charts__seg-btn${mode === 'computed' ? ' is-active' : ''}`}
              onClick={() => setMode('computed')}
              title="Ein Wert je Intervall; was gerechnet wird, steht je Serie in der Serienliste"
            >
              {COMPUTED_LABEL}
            </button>
          </div>
          <select
            class="hv-charts__raster-select"
            value={raster}
            disabled={mode !== 'computed'}
            aria-label="Intervall"
            title={mode === 'computed'
              ? 'Intervall: Länge der Abschnitte, für die je Serie ein Wert berechnet wird. Auto wird beim Zoomen feiner.'
              : 'Intervall: nur bei „Berechnet“; Rohdaten zeigen jede Meldung.'}
            onChange={(e) => setRaster((e.currentTarget as HTMLSelectElement).value as RasterKey)}
          >
            <option value="auto">Auto ({formatIntervalMs(detail?.key === detailKey && detail.intervalMs ? detail.intervalMs : autoIntervalMs(windowMs))})</option>
            {RASTERS.map((r) => (
              <option key={r.key} value={r.key} disabled={windowMs / r.ms > MAX_RASTER_INTERVALS}>
                {formatIntervalMs(r.ms)}
              </option>
            ))}
          </select>
          <ToolGroup label="Live">
            <ToolButton
              label={liveRefresh ? 'Live aus' : 'Live: alle 30 Sekunden neu laden, sinnvoll für 1 h und 24 h'}
              active={liveRefresh}
              onClick={() => setLiveRefresh((v) => !v)}
            >
              <span class={`hv-charts__live-icon${liveRefresh && historyBusy ? ' is-busy' : ''}`}><IconLive size={18} /></span>
            </ToolButton>
          </ToolGroup>
          <div class="hv-charts__toolbar-spacer" />
          <ToolGroup label="Anmerkungen">
            <ToolButton
              label="Bereich im Chart per Ziehen markieren, dann Kommentar hinzufügen"
              disabled={chartSeries.length === 0}
              onClick={() => chartHandleRef.current?.enterBrushMode()}
            >
              <IconBrushRange size={18} />
            </ToolButton>
            <ToolButton
              label={`Anmerkungen der Anlage (${annotations.length})`}
              active={showAnnotationList}
              onClick={() => setShowAnnotationList((v) => !v)}
            >
              <span class="hv-charts__badge-wrap">
                <IconComment size={18} />
                {annotations.length > 0 && <span class="hv-charts__badge">{annotations.length}</span>}
              </span>
            </ToolButton>
          </ToolGroup>
          <ToolGroup label="Ausgabe">
            <ToolButton label="Als Bild sichern (PNG)" disabled={chartSeries.length === 0} onClick={exportPng}>
              <IconImage size={18} />
            </ToolButton>
            <ToolButton
              label={exportBusy ? 'CSV wird erstellt…' : 'CSV: exakte Intervallwerte mit Einheit, Abdeckung und Hinweisen'}
              disabled={chartSeries.length === 0 || exportBusy}
              onClick={() => void exportCsv()}
            >
              <IconDownload size={18} />
            </ToolButton>
            <ToolButton
              label="Puffer: gepufferten Verlauf des Field-Clients ansehen oder übernehmen"
              disabled={selectedIds.length === 0}
              onClick={() => setBufferOpen(true)}
            >
              <IconBuffer size={18} />
            </ToolButton>
            {!isFullscreen && (
              <ToolButton label="In neuem Tab als Vollbild öffnen" onClick={openFullscreen}>
                <IconFullscreen size={18} />
              </ToolButton>
            )}
          </ToolGroup>
        </div>

        <div class={`hv-charts__body${view.picker ? '' : ' hv-charts__body--rail'}`}>
          {!isFullscreen && !view.picker && (
            <button type="button" class="hv-charts__picker-rail" onClick={() => toggleView('picker')}
                    title="Datenpunkte einblenden" aria-label="Datenpunkte einblenden">
              <IconSidebar size={18} />
              <span class="hv-charts__picker-rail-text">Datenpunkte · {selectedIds.length} ausgewählt</span>
            </button>
          )}
          {!isFullscreen && view.picker && (
            <div class="hv-charts__picker-col">
              {schemaState.status === 'loading' && <p class="muted">Schema wird geladen…</p>}
              {schemaState.status === 'error' && (
                <p class="hv-charts__error">Anlagen-Schema konnte nicht geladen werden.</p>
              )}
              {schemaState.status === 'ok' && (
                <MultiLeafPicker
                  schema={schemaState.data}
                  selectedIds={selectedIds}
                  onChange={setSelectedIds}
                />
              )}
            </div>
          )}
          <div class="hv-charts__chart-col">
            <div ref={chartCanvasRef} class="hv-charts__chart-canvas" style={{ position: 'relative' }}>
              {historyBusy && (
                <div class="hv-charts__busy muted">Verlauf lädt…</div>
              )}
              {!historyBusy && detailBusy && (
                <div class="hv-charts__busy muted">Ausschnitt lädt…</div>
              )}
              {chartSeries.length === 0 ? (
                <div class="hv-charts__placeholder muted">
                  Datenpunkte im Picker links auswählen, um einen Chart zu erzeugen.
                </div>
              ) : (
                <MultiSeriesChart
                  ref={chartHandleRef}
                  series={chartSeries}
                  annotations={annotationOverlays}
                  window={chartWindow}
                  onZoom={setZoomed}
                  onBrushRange={(rangeMs) => {
                    // Brush end delivers absolute ms since epoch already.
                    setCreatorSeed({ range: rangeMs });
                  }}
                  onAnnotationClick={(annotationId) => {
                    const hit = annotations.find((a) => a.id === annotationId);
                    if (hit) setDetailAnnotation(hit);
                  }}
                />
              )}
              {chartEmpty && (
                <div class="hv-charts__empty muted">
                  {invalidOnly > 0
                    ? `Kein gültiger Wert im Zeitraum: ${invalidOnly.toLocaleString('de-AT')} ${invalidOnly === 1 ? 'Meldung' : 'Meldungen'} als ungültig verworfen (ausserhalb des gültigen Bereichs oder vom Regler als Fehler gemeldet, z. B. Fühlerfehler).`
                    : 'Keine Daten im Zeitraum.'}
                  {lastReport && ` Letzte Meldung: ${new Date(lastReport).toLocaleString('de-AT')}.`}
                </div>
              )}
              {creatorSeed && (
                <div class="hv-charts__ann-popover" style={{ position: 'absolute', top: 12, right: 12 }}>
                  <AnnotationCreator
                    plantId={plantId}
                    seed={creatorSeed}
                    leafId={null}
                    onSaved={(created) => {
                      setAnnotations((prev) => [...prev, created]);
                      setCreatorSeed(null);
                      setAnnotationsRefreshKey((k) => k + 1);
                    }}
                    onCancel={() => setCreatorSeed(null)}
                  />
                </div>
              )}
              {detailAnnotation && (
                <div class="hv-charts__ann-popover" style={{ position: 'absolute', top: 12, right: 12 }}>
                  <div class="hv-ann-detail">
                    <div class="hv-ann-detail__header">
                      <div>
                        <div class="hv-ann-detail__author">{detailAnnotation.authorDisplayName}</div>
                        <div class="hv-ann-detail__at muted">
                          {new Date(detailAnnotation.atUtc).toLocaleString()}
                          {detailAnnotation.untilUtc && ' — ' + new Date(detailAnnotation.untilUtc).toLocaleString()}
                        </div>
                      </div>
                      <button
                        type="button"
                        class="hv-btn hv-btn--sm hv-btn--ghost"
                        onClick={() => setDetailAnnotation(null)}
                      >
                        Schließen
                      </button>
                    </div>
                    <div class="hv-ann-detail__body">
                      <MentionMarkdown body={detailAnnotation.body} />
                    </div>
                  </div>
                </div>
              )}
              {showAnnotationList && (
                <div class="hv-charts__ann-popover" style={{ position: 'absolute', top: 12, right: 12, maxWidth: 520, maxHeight: '80%', overflow: 'auto', background: 'var(--surface-panel)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', padding: 'var(--space-3)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-2)' }}>
                    <strong>Anmerkungen dieser Anlage</strong>
                    <button type="button" class="hv-btn hv-btn--sm hv-btn--ghost" onClick={() => setShowAnnotationList(false)}>Schließen</button>
                  </div>
                  <AnnotationList
                    plantId={plantId}
                    currentUserId={sessionSignal.value.user?.userId ?? null}
                    refreshKey={annotationsRefreshKey}
                    onJump={(r) => {
                      setDetailAnnotation(r);
                      setShowAnnotationList(false);
                      const start = new Date(r.atUtc).getTime();
                      const end = r.untilUtc ? new Date(r.untilUtc).getTime() : start;
                      const span = Math.max(end - start, 60 * 60_000);
                      const pad = span * 0.25;
                      setRange({
                        key: 'custom',
                        from: toLocalIsoNoMs(new Date(start - pad)),
                        to: toLocalIsoNoMs(new Date(end + pad)),
                      });
                    }}
                  />
                </div>
              )}
            </div>
            {!isFullscreen && view.series && (
              <div class="hv-charts__editor">
                <SeriesEditor
                  rows={editorRows}
                  computed={mode === 'computed'}
                  onPatch={patchSeries}
                  onRemove={removeSeries}
                />
              </div>
            )}
            {!isFullscreen && view.kpi && kpiRows.length > 0 && (
              <div class="hv-charts__kpi">
                <KeyFigures
                  rows={kpiRows}
                  window={kpiWindow}
                  effective={kpiRange}
                  zoomed={zoomed !== null}
                  busy={kpiBusy}
                  exportName={presetFilename('', 'kennzahlen')}
                  onWindowChange={setKpiWindow}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {bufferOpen && (
        <CovBufferModal
          plantId={plantId}
          leaves={selectedIds
            .map((id) => leafById.get(id))
            .filter((leaf): leaf is LeafDto => leaf !== undefined)}
          initialRangeMs={[
            computeRange(range).from.getTime(),
            computeRange(range).to.getTime(),
          ]}
          onClose={() => setBufferOpen(false)}
        />
      )}
    </AppShell>
  );
}

// --- helpers --------------------------------------------------------------
function toLocalIsoNoMs(d: Date): string {
  // datetime-local input wants yyyy-MM-ddTHH:mm — no timezone, no seconds.
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// --- Ansicht merken -------------------------------------------------------
interface ChartView { picker: boolean; series: boolean; kpi: boolean }

const CHART_VIEW_KEY = 'hv.charts.ansicht';
/** Erstes Oeffnen: Datenpunkte offen, Serien und Kennzahlen zu. */
const DEFAULT_CHART_VIEW: ChartView = { picker: true, series: false, kpi: false };

function readChartView(): ChartView {
  try {
    const raw = localStorage.getItem(CHART_VIEW_KEY);
    if (!raw) return DEFAULT_CHART_VIEW;
    const v = JSON.parse(raw) as Partial<ChartView>;
    return {
      picker: typeof v.picker === 'boolean' ? v.picker : DEFAULT_CHART_VIEW.picker,
      series: typeof v.series === 'boolean' ? v.series : DEFAULT_CHART_VIEW.series,
      kpi: typeof v.kpi === 'boolean' ? v.kpi : DEFAULT_CHART_VIEW.kpi,
    };
  } catch {
    return DEFAULT_CHART_VIEW;
  }
}

function writeChartView(v: ChartView): void {
  try { localStorage.setItem(CHART_VIEW_KEY, JSON.stringify(v)); } catch { /* privates Fenster: dann eben nicht */ }
}
